import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Form, Badge, Spinner } from 'react-bootstrap';
import ccmService from '../../services/ccmService';
import careProgramService from '../../services/careProgramService';
import './EnrollmentQueue.css';

const EnrollmentQueue = () => {
  const [loading, setLoading] = useState(true);
  const [patients, setPatients] = useState([]);
  const [carePrograms, setCarePrograms] = useState([]);
  const [pagination, setPagination] = useState({});
  const [filters, setFilters] = useState({
    practiceId: '',
    status: '',
    programType: '',
    page: 1,
    perPage: 20
  });

  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const response = await careProgramService.getCarePrograms();
        if (response.success) {
          setCarePrograms(response.data || []);
        }
      } catch (err) {
        console.error('Failed to fetch care programs:', err);
      }
    };
    fetchPrograms();
  }, []);

  useEffect(() => {
    fetchEnrollmentQueue();
  }, [filters]);

  const fetchEnrollmentQueue = async () => {
    try {
      setLoading(true);
      const response = await ccmService.getEnrollmentQueue(filters);
      if (response.success) {
        setPatients(response.data || []);
        setPagination(response.pagination || {});
      }
    } catch (error) {
      console.error('Error fetching enrollment queue:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: 1 // Reset to first page when filter changes
    }));
  };

  const handleEnroll = (patientId) => {
    // Navigate to enrollment form
    window.location.href = `/ccm/enroll/${patientId}`;
  };

  const getStatusBadge = (status) => {
    const variants = {
      'active': 'success',
      'pending': 'warning',
      'disenrolled': 'secondary',
      'declined': 'danger'
    };
    return <Badge bg={variants[status] || 'secondary'}>{status || 'Eligible'}</Badge>;
  };

  return (
    <Container fluid className="ccm-eq-container">
      <Row>
        <Col>
          <Card>
            <Card.Header>
              <h4>CCM Enrollment Queue</h4>
            </Card.Header>
            <Card.Body>
              {/* Filters */}
              <Row className="mb-3">
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Practice</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Practice ID"
                      value={filters.practiceId}
                      onChange={(e) => handleFilterChange('practiceId', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      value={filters.status}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                    >
                      <option value="">All</option>
                      <option value="pending">Pending</option>
                      <option value="active">Active</option>
                      <option value="disenrolled">Disenrolled</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Program Type</Form.Label>
                    <Form.Select
                      value={filters.programType}
                      onChange={(e) => handleFilterChange('programType', e.target.value)}
                    >
                      <option value="">All</option>
                      {carePrograms.map(prog => (
                        <option key={prog.id} value={prog.short_name}>
                          {prog.short_name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3} className="d-flex align-items-end">
                  <Button variant="primary" onClick={fetchEnrollmentQueue}>
                    Filter
                  </Button>
                </Col>
              </Row>

              {/* Table */}
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" />
                </div>
              ) : (
                <>
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>Patient Name</th>
                        <th>DOB</th>
                        <th>Age</th>
                        <th>Status</th>
                        <th>Program Type</th>
                        <th>Chronic Conditions</th>
                        <th>ICD Codes</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {patients.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="text-center">
                            No patients found in enrollment queue
                          </td>
                        </tr>
                      ) : (
                        patients.map((patient) => (
                          <tr key={patient.patient_id}>
                            <td>{patient.patient_name}</td>
                            <td>{patient.date_of_birth ? new Date(patient.date_of_birth).toLocaleDateString() : 'N/A'}</td>
                            <td>{patient.age || 'N/A'}</td>
                            <td>{getStatusBadge(patient.enrollment_status)}</td>
                            <td>{patient.program_type || 'N/A'}</td>
                            <td>{patient.chronic_conditions_count || 0}</td>
                            <td>{patient.icd_codes || 'N/A'}</td>
                            <td>
                              {!patient.enrollment_id && (
                                <Button
                                  size="sm"
                                  variant="success"
                                  onClick={() => handleEnroll(patient.patient_id)}
                                >
                                  Enroll
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>

                  {/* Pagination */}
                  {pagination.total_pages > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <div>
                        Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to{' '}
                        {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of{' '}
                        {pagination.total} entries
                      </div>
                      <div>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          disabled={pagination.current_page === 1}
                          onClick={() => handleFilterChange('page', pagination.current_page - 1)}
                        >
                          Previous
                        </Button>
                        <span className="mx-2">
                          Page {pagination.current_page} of {pagination.total_pages}
                        </span>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          disabled={pagination.current_page === pagination.total_pages}
                          onClick={() => handleFilterChange('page', pagination.current_page + 1)}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default EnrollmentQueue;

